import React, { useEffect, useState } from 'react';
import { Modal } from '../ui';
import type { PlatformOrganization, SubscriptionTier } from '../../types/platformOrganization';
import { OrganizationUsageBar } from './OrganizationUsageBar';

const TIER_OPTIONS: { value: SubscriptionTier; label: string; price: number }[] = [
  { value: 'BASIC', label: 'Básico', price: 20 },
  { value: 'PROFESSIONAL', label: 'Profesional', price: 50 },
  { value: 'PREMIUM', label: 'Premium', price: 100 },
];

interface OrganizationPlanModalProps {
  isOpen: boolean;
  mode: 'create' | 'edit';
  organization?: PlatformOrganization | null;
  onClose: () => void;
  onSubmit: (payload: { name: string; tier: SubscriptionTier }) => void;
  isLoading?: boolean;
}

export const OrganizationPlanModal: React.FC<OrganizationPlanModalProps> = ({
  isOpen,
  mode,
  organization,
  onClose,
  onSubmit,
  isLoading,
}) => {
  const [name, setName] = useState('');
  const [tier, setTier] = useState<SubscriptionTier>('BASIC');

  useEffect(() => {
    if (!isOpen) return;
    if (mode === 'edit' && organization) {
      setName(organization.name || organization.organizationName || '');
      setTier(organization.tier);
    } else {
      setName('');
      setTier('BASIC');
    }
  }, [isOpen, mode, organization]);

  const title = mode === 'create' ? 'Nueva cuenta de facturación' : 'Editar plan y cuenta';

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} size="md">
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit({ name: name.trim(), tier });
        }}
      >
        <div>
          <label className="block text-xs font-medium text-slate-700 mb-1">Nombre de la cuenta</label>
          <input
            required
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Ej. Grupo Funerario del Norte"
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-slate-700 mb-1">Plan de suscripción</label>
          <select
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
            value={tier}
            onChange={(e) => setTier(e.target.value as SubscriptionTier)}
          >
            {TIER_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label} — ${opt.price}/mes
              </option>
            ))}
          </select>
        </div>

        {mode === 'edit' && organization && (
          <div className="rounded-xl border border-slate-200 bg-slate-50/80 p-4 space-y-2.5">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Uso actual (no se puede bajar de plan si se exceden límites)
            </p>
            <OrganizationUsageBar
              label="Funerarias"
              used={organization.usage.tenants}
              max={organization.limits.maxTenants}
              compact
            />
            <OrganizationUsageBar
              label="Usuarios"
              used={organization.usage.users}
              max={organization.limits.maxUsers}
              compact
            />
            <OrganizationUsageBar
              label="Clientes"
              used={organization.usage.activeClients}
              max={organization.limits.maxActiveClients}
              compact
            />
            <OrganizationUsageBar
              label="Servicios este mes"
              used={organization.usage.servicesThisMonth}
              max={organization.limits.maxServicesPerMonth}
              compact
            />
            {organization.tenants.length > 0 && (
              <p className="text-xs text-slate-500 pt-1">
                Funerarias: {organization.tenants.map((t) => t.name).join(', ')}
              </p>
            )}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            className="px-4 py-2 rounded-lg text-sm text-slate-600 hover:bg-slate-100"
            onClick={onClose}
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={isLoading}
            className="px-4 py-2 rounded-lg text-sm font-semibold bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50"
          >
            {mode === 'create' ? 'Crear cuenta' : 'Guardar cambios'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default OrganizationPlanModal;
